import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { OperationError } from "../DAO";
import { Instance } from "./Instance";
import { listInstances } from "./InstanceDAO";

export type InstanceStore = {
   readonly isLoading: boolean;
   readonly instances: Instance[] | null;
   readonly error: OperationError | null;

   readonly isStale: boolean;
   readonly markStale: () => void;
   readonly loadInstances: () => Promise<void>;
};

const instanceContext = createContext<InstanceStore>(null as never);

type Props = {
   children?: React.ReactNode;
};
export const InstanceProvider = ({ children }: Props): JSX.Element => {
   const [instances, setInstances] = useState<Instance[] | null>(null);
   const [error, setError] = useState<OperationError | null>(null);
   const [isStale, setStale] = useState<boolean>(false);
   const [promise, setPromise] = useState<Promise<void> | null>(null);

   const isLoading: boolean = useMemo(() => promise !== null, [promise]);

   async function loadInstances(): Promise<void> {
      if (promise) { // Already doing a fetch, use that one
         return promise;
      }
      setError(null);
      // Note:  Not clearing existing instances array until loading finishes
      const newPromise = listInstances()
         .catch((err: OperationError) => {
            setError(err);
            return null;
         })
         .then((results: Instance[] | null) => {
            setInstances(results);
            setStale(false);
            setPromise(null);
         });
      setPromise(newPromise);
      return newPromise;
   }

   const markStale = (): void => setStale(true);

   useEffect(() => {
      // initialize
      if (instances == null && error == null && !isLoading) {
         loadInstances();
      }
   }, []);

   return (
      <instanceContext.Provider value={{ isLoading, instances, error, isStale, markStale, loadInstances }}>
         {children}
      </instanceContext.Provider>
   );
}

export const useInstanceStore = (): InstanceStore => {
   return useContext(instanceContext);
};
